import React, { FC, Fragment, MouseEvent, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, List as MuiList, Typography } from '@material-ui/core';

import { SearchDocuments } from 'api';
import { ClientSettingParams, IFilter } from 'types/app';
import { getSelectedFilterTitle } from 'helpers';
import { useProtectedClient } from 'hooks';
import * as paths from 'paths';
import * as Links from 'Links';
import {
  DepartmentFilters,
  Desktop,
  InviteDialog,
  LoaderComponents,
  LocationFilters,
  Mobile,
  MobileSubmenu,
  Queries,
  SidebarItem,
  StickySidebar,
} from 'components';
import { SidebarButton } from 'components/Buttons';

const FilterSelect: FC = () => {
  const params = useParams<ClientSettingParams>();
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();
  const client = useProtectedClient();

  const isPeople = pathname === paths.team;
  const isPeopleFollowing = pathname === paths.teamFollowing;
  const isPeopleEveryone = pathname === paths.teamEveryone;

  const filters: IFilter[] = [
    {
      component: Links.Team,
      isSelected: isPeople,
      textNode: t('common:recommended'),
    },
    {
      component: Links.TeamFollowing,
      isSelected: isPeopleFollowing,
      textNode: t('common:following'),
    },
    {
      component: Links.TeamEveryone,
      isSelected: isPeopleEveryone,
      textNode: t('common:everyone'),
    },
  ];

  function toggleExpanded(isExpanded: boolean) {
    setExpanded(isExpanded);
  }

  function openInvite(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function closeInvite() {
    setAnchorEl(null);
  }

  function renderInviteButton() {
    if (!client.allow_user_invites) {
      return null;
    }

    return <SidebarButton onClick={openInvite}>{t('invites:send')}</SidebarButton>;
  }

  function renderFilter(filter: IFilter, index: number) {
    const { component, isSelected, textNode } = filter;

    return (
      <SidebarItem
        color="inherit"
        key={index}
        link={component}
        isSelected={isSelected}
        textNode={textNode}
      />
    );
  }

  function renderLoader() {
    const { Content, List, Row } = LoaderComponents;

    return (
      <List rows={8}>
        {rowIndex => (
          <Row padding="8px 12px">
            <Grid item>
              <Content index={rowIndex} maxWidth={140} />
            </Grid>
          </Row>
        )}
      </List>
    );
  }

  function renderDesktop() {
    return (
      <StickySidebar>
        {renderInviteButton()}
        <MuiList component="nav">{filters.map(renderFilter)}</MuiList>
        <Queries.SearchAggregates document={SearchDocuments.Profiles} loader={renderLoader()}>
          {({ departments, locations }) => (
            <Fragment>
              <DepartmentFilters departments={departments} context="team" />
              <LocationFilters locations={locations} context="team" />
            </Fragment>
          )}
        </Queries.SearchAggregates>
        <InviteDialog open={!!anchorEl} onClose={closeInvite} />
      </StickySidebar>
    );
  }

  function renderMobile() {
    return (
      <Queries.SearchAggregates document={SearchDocuments.Profiles} loader={renderLoader()}>
        {searchAggregates => {
          const filterTitle = getSelectedFilterTitle(filters, searchAggregates, params);

          return (
            <MobileSubmenu
              expanded={expanded}
              onToggle={toggleExpanded}
              selected={<Typography variant="subtitle2">{filterTitle}</Typography>}
            >
              <MuiList component="nav">{filters.map(renderFilter)}</MuiList>
              <DepartmentFilters departments={searchAggregates.departments} context="team" />
              <LocationFilters locations={searchAggregates.locations} context="team" />
              {renderInviteButton()}
              <InviteDialog open={!!anchorEl} onClose={closeInvite} />
            </MobileSubmenu>
          );
        }}
      </Queries.SearchAggregates>
    );
  }

  return (
    <Fragment>
      <Desktop>{renderDesktop()}</Desktop>
      <Mobile>{renderMobile()}</Mobile>
    </Fragment>
  );
};

export default FilterSelect;
